import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Lock, Zap, Activity } from 'lucide-react';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DivergenceGaugeProps {
  /** Filtered Sentiment ILR [0–1]  — "The Vibe" */
  vibe: number;
  /** Normalised AMM market price [0–1]  — "The Vault" */
  vault: number;
  market?: string;
  live?: boolean;
  /** Pass a fresh Date() each tick to trigger the pulse animation */
  lastUpdate?: Date;
}

// ─── SVG helpers ──────────────────────────────────────────────────────────────
//
//  Gauge is a 180° clockwise arc:
//    180° = 9 o'clock = 0.00   (left)
//    270° = 12 o'clock = 0.50  (top)
//    360° = 3 o'clock  = 1.00  (right)

const CX = 250, CY = 252, R = 178, SW = 15;
const VW = 500, VH = 272;
const MONO = '"JetBrains Mono", "Cascadia Code", ui-monospace, monospace';

const toAngle = (v: number) => 180 + Math.max(0, Math.min(1, v)) * 180;

const pt = (deg: number, r = R) => ({
  x: CX + r * Math.cos((deg * Math.PI) / 180),
  y: CY + r * Math.sin((deg * Math.PI) / 180),
});

const arc = (a1: number, a2: number, r = R): string => {
  if (a2 - a1 < 0.02) return '';
  const s = pt(a1, r), e = pt(a2, r);
  return `M ${s.x.toFixed(2)},${s.y.toFixed(2)} A ${r},${r} 0 ${a2 - a1 > 180 ? 1 : 0} 1 ${e.x.toFixed(2)},${e.y.toFixed(2)}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

const DivergenceGauge: React.FC<DivergenceGaugeProps> = ({
  vibe       = 0.82,
  vault      = 0.59,
  market     = 'Norwich City v QPR',
  live       = true,
  lastUpdate,
}) => {
  const [prog, setProg]       = useState(0);      // mount sweep [0 → 1]
  const [pulsing, setPulsing] = useState(false);  // data-refresh flash
  const [hover, setHover]     = useState(false);  // gap arc hover
  const [tip, setTip]         = useState({ x: 0, y: 0 });

  const svgRef    = useRef<SVGSVGElement>(null);
  const mounted   = useRef(false);
  const skipFirst = useRef(true);

  // Entry animation — one-shot ease-out cubic on mount
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    let start: number;
    const step = (ts: number) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / 1400, 1);
      setProg(1 - Math.pow(1 - t, 3));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  // Pulse on every data tick after the initial render
  useEffect(() => {
    if (skipFirst.current) { skipFirst.current = false; return; }
    if (!lastUpdate) return;
    setPulsing(true);
    const id = window.setTimeout(() => setPulsing(false), 600);
    return () => window.clearTimeout(id);
  }, [lastUpdate]);

  // Animated angles
  const vaultAng = 180 + prog * (toAngle(vault) - 180);
  const vibeAng  = 180 + prog * (toAngle(vibe)  - 180);

  // Gap always between the lower and higher of the two values
  const gapA1 = Math.min(vaultAng, vibeAng);
  const gapA2 = Math.max(vaultAng, vibeAng);

  const divNum = vault - vibe;                                   // positive = price leads anchor
  const divStr = (divNum >= 0 ? '+' : '') + divNum.toFixed(2);
  const pct    = Math.round(Math.abs(divNum) * 100);

  const glowing = hover || pulsing;

  const onMove = (e: React.MouseEvent<SVGPathElement>) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (r) setTip({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  // Tick radii
  const TI = R + SW / 2 + 3;   // inner edge
  const TM = R + SW / 2 + 16;  // major outer
  const Tm = R + SW / 2 + 9;   // minor outer
  const TL = R + SW / 2 + 30;  // label

  return (
    <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl overflow-hidden w-full max-w-3xl mx-auto select-none">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-[#2a2a2a] bg-[#1a1a1a]">
        <div>
          <p className="text-[8px] font-mono tracking-[0.22em] text-[#4b6080]/60 uppercase mb-0.5">
            Norwich Hub  ·  Market Intelligence
          </p>
          <h2 className="text-[13px] font-semibold text-[#e2e8f0] tracking-tight">
            {market}
          </h2>
        </div>
        {live && (
          <div className="flex items-center gap-2">
            <Activity size={13} className="text-[#00d4ff]" />
            <span
              className="w-[6px] h-[6px] rounded-full bg-[#00d4ff] animate-pulse"
              style={{ boxShadow: '0 0 6px #00d4ff' }}
            />
            <span className="text-[9px] font-mono tracking-[0.16em] text-[#00d4ff]">
              LIVE
            </span>
          </div>
        )}
      </header>

      {/* ── Gauge ──────────────────────────────────────────────────────────── */}
      <div className="relative px-4 pt-0 pb-0">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VW} ${VH}`}
          className="block mx-auto"
          style={{ overflow: 'visible', maxHeight: '185px', width: 'auto' }}
          aria-label="Divergence gauge"
        >
          <defs>
            {/* Electric-blue bloom — widens when pulsing */}
            <filter id="gap-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur in="SourceGraphic" stdDeviation={pulsing ? '9' : '5'} result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            {/* Soft needle glow */}
            <filter id="needle-glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer decorative ring */}
          <circle cx={CX} cy={CY} r={R + SW / 2 + 2}
            fill="none" stroke="#1e1e1e" strokeWidth={1} strokeDasharray="3 8" />

          {/* Track — full empty arc */}
          <path d={arc(180, 360)} fill="none"
            stroke="#1a1a1a" strokeWidth={SW} strokeLinecap="butt" />

          {/* Vault arc — amber, 0 → vault position */}
          {arc(180, vaultAng) && (
            <path d={arc(180, vaultAng)} fill="none"
              stroke="#b45309" strokeWidth={SW} strokeLinecap="butt" opacity={0.65}
              style={{ transition: 'opacity 0.3s' }} />
          )}

          {/* Divergence gap — electric blue + bloom */}
          {arc(gapA1, gapA2) && (
            <>
              {/* Bloom layer */}
              <path d={arc(gapA1, gapA2)} fill="none"
                stroke="#00d4ff" strokeWidth={SW + 6} strokeLinecap="butt"
                opacity={pulsing ? 0.35 : 0.20}
                filter="url(#gap-glow)"
                style={{ transition: 'opacity 0.3s' }} />
              {/* Crisp arc — interaction target */}
              <path
                d={arc(gapA1, gapA2)}
                fill="none" stroke="#00d4ff"
                strokeWidth={SW} strokeLinecap="butt"
                opacity={glowing ? 1 : 0.90}
                style={{ cursor: 'crosshair', transition: 'opacity 0.15s' }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onMouseMove={onMove}
              />
            </>
          )}

          {/* Minor ticks */}
          {[0.1, 0.2, 0.3, 0.4, 0.6, 0.7, 0.8, 0.9].map((v) => {
            const a = toAngle(v), o = pt(a, TI), i = pt(a, Tm);
            return <line key={v} x1={o.x} y1={o.y} x2={i.x} y2={i.y}
              stroke="#242424" strokeWidth={1} />;
          })}

          {/* Major ticks + labels */}
          {[0, 0.25, 0.5, 0.75, 1.0].map((v) => {
            const a = toAngle(v);
            const o = pt(a, TI), m = pt(a, TM), l = pt(a, TL);
            return (
              <g key={v}>
                <line x1={o.x} y1={o.y} x2={m.x} y2={m.y}
                  stroke="#2e2e2e" strokeWidth={1.5} />
                <text x={l.x} y={l.y}
                  textAnchor="middle" dominantBaseline="middle"
                  fill="#333" fontSize={9.5} fontFamily={MONO}>
                  {v.toFixed(2)}
                </text>
              </g>
            );
          })}

          {/* Vault needle — amber */}
          {(() => {
            const t = pt(vaultAng, R - SW / 2 - 4);
            const b = pt(vaultAng, 26);
            return (
              <g filter="url(#needle-glow)">
                <line x1={b.x} y1={b.y} x2={t.x} y2={t.y}
                  stroke="#f59e0b" strokeWidth={2} strokeLinecap="round" />
                <circle cx={t.x} cy={t.y} r={4.5} fill="#f59e0b" />
              </g>
            );
          })()}

          {/* Vibe needle — emerald */}
          {(() => {
            const t = pt(vibeAng, R - SW / 2 - 4);
            const b = pt(vibeAng, 26);
            return (
              <g filter="url(#needle-glow)">
                <line x1={b.x} y1={b.y} x2={t.x} y2={t.y}
                  stroke="#10b981" strokeWidth={2} strokeLinecap="round" />
                <circle cx={t.x} cy={t.y} r={4.5} fill="#10b981" />
              </g>
            );
          })()}

          {/* Hub cap */}
          <circle cx={CX} cy={CY} r={22} fill="#121212" stroke="#2a2a2a" strokeWidth={1.5} />
          <circle cx={CX} cy={CY} r={5}  fill="#1f1f1f" />

          {/* Centre readout */}
          <text x={CX} y={CY - 86} textAnchor="middle"
            fill="#2a2a2a" fontSize={9} letterSpacing={3} fontFamily={MONO}>
            DIVERGENCE
          </text>
          <text x={CX} y={CY - 60} textAnchor="middle"
            fill={glowing ? '#00d4ff' : '#1a3a4a'}
            fontSize={30} fontWeight="700" fontFamily={MONO}
            style={{
              transition: 'fill 0.25s, filter 0.25s',
              filter: glowing ? 'drop-shadow(0 0 14px #00d4ff)' : 'none',
            }}>
            {prog > 0.05
              ? (divNum >= 0 ? '+' : '') + (divNum * prog).toFixed(2)
              : '+0.00'}
          </text>
          <text x={CX} y={CY - 40} textAnchor="middle"
            fill={glowing ? '#00d4ff' : '#222'} fontSize={12} fontWeight="600"
            fontFamily={MONO}
            style={{ transition: 'fill 0.25s' }}>
            {pct}%
          </text>
        </svg>

        {/* Tooltip */}
        {hover && (
          <div
            className="pointer-events-none absolute z-30 px-3 py-2 rounded-lg
                       bg-[#121212]/95 border border-[#00d4ff]/25"
            style={{
              left: tip.x, top: tip.y - 56,
              transform: 'translateX(-50%)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 0 20px rgba(0,212,255,0.15)',
            }}
          >
            <p className="text-[11px] font-mono text-[#00d4ff] whitespace-nowrap">
              Crowd Conviction vs. Capital Commitment
            </p>
            <p className="text-[8.5px] font-mono text-[#00d4ff]/40 mt-1 text-center">
              Δ {Math.abs(divNum).toFixed(2)} · {pct}pp
            </p>
          </div>
        )}
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 divide-x divide-[#2a2a2a] px-0 py-0 border-t border-[#2a2a2a]">

        {/* The Vibe */}
        <div className="flex items-center gap-2 px-3 py-2">
          <TrendingUp size={14} className="text-[#10b981] shrink-0" />
          <div>
            <p className="text-[7px] font-mono tracking-[0.18em] text-[#4b6080]/50 uppercase">
              The Vibe
            </p>
            <p className="text-[16px] font-mono font-bold text-[#10b981] leading-none mt-0.5"
              style={{
                filter: pulsing ? 'drop-shadow(0 0 8px rgba(16,185,129,0.6))' : 'none',
                transition: 'filter 0.3s',
              }}>
              {vibe.toFixed(2)}
            </p>
            <p className="text-[7px] font-mono text-[#4b6080]/40 mt-0.5">
              Filtered Sentiment · ILR
            </p>
          </div>
        </div>

        {/* Divergence */}
        <div className="flex items-center justify-center gap-2 px-3 py-2">
          <Zap size={14} className="text-[#00d4ff] shrink-0" />
          <div className="text-center">
            <p className="text-[7px] font-mono tracking-[0.18em] text-[#4b6080]/50 uppercase">
              Divergence
            </p>
            <p className="text-[16px] font-mono font-bold text-[#00d4ff] leading-none mt-0.5"
              style={{
                filter: glowing ? 'drop-shadow(0 0 10px rgba(0,212,255,0.7))' : 'none',
                transition: 'filter 0.3s',
              }}>
              {divStr}
            </p>
            <p className="text-[7px] font-mono text-[#4b6080]/40 mt-0.5">
              Market Opportunity
            </p>
          </div>
        </div>

        {/* The Vault */}
        <div className="flex items-center justify-end gap-2 px-3 py-2">
          <div className="text-right">
            <p className="text-[7px] font-mono tracking-[0.18em] text-[#4b6080]/50 uppercase">
              The Vault
            </p>
            <p className="text-[16px] font-mono font-bold text-[#f59e0b] leading-none mt-0.5"
              style={{
                filter: pulsing ? 'drop-shadow(0 0 8px rgba(245,158,11,0.5))' : 'none',
                transition: 'filter 0.3s',
              }}>
              {vault.toFixed(2)}
            </p>
            <p className="text-[7px] font-mono text-[#4b6080]/40 mt-0.5">
              AMM Price · Capital
            </p>
          </div>
          <Lock size={14} className="text-[#f59e0b] shrink-0" />
        </div>
      </div>

    </div>
  );
};

export default DivergenceGauge;
